'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'

export default function SearchBox404() {
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const router = useRouter()

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!searchQuery.trim()) return
    
    setIsSearching(true)
    
    try {
      // Encode the search query for URL
      const encodedQuery = encodeURIComponent(searchQuery.trim())
      
      // Determine search strategy based on query content
      const query = searchQuery.toLowerCase().trim()
      
      // Check if query contains team names or match-related terms
      const teamKeywords = ['barcelona', 'real madrid', 'manchester', 'liverpool', 'chelsea', 'arsenal', 'tottenham', 'city', 'united', 'psg', 'juventus', 'milan', 'inter', 'bayern', 'dortmund', 'atletico', 'sevilla', 'valencia', 'napoli', 'roma', 'lazio', 'atalanta', 'fiorentina', 'ajax', 'psv', 'benfica', 'porto', 'sporting', 'galatasaray', 'fenerbahce', 'olympiakos', 'panathinaikos', 'celtic', 'rangers', 'dynamo', 'shakhtar', 'zenit', 'cska', 'spartak', 'lokomotiv', 'slavia', 'viktoria', 'red star', 'partizan', 'anderlecht', 'club brugge', 'standard', 'genk', 'lyon', 'marseille', 'monaco', 'nice', 'rennes', 'lille', 'nantes', 'montpellier', 'strasbourg', 'lens', 'brest', 'angers', 'reims', 'metz', 'dijon', 'nimes', 'amiens', 'caen', 'guingamp', 'toulouse', 'bordeaux', 'saint-etienne', 'wolves', 'leicester', 'everton', 'aston villa', 'newcastle', 'brighton', 'crystal palace', 'burnley', 'sheffield', 'norwich', 'watford', 'bournemouth', 'fulham', 'west ham', 'southampton', 'leeds', 'brentford', 'vietnam', 'việt nam', 'v-league', 'viettel', 'hanoi', 'hà nội', 'hcm', 'sài gòn', 'saigon', 'đà nẵng', 'danang', 'hải phòng', 'haiphong', 'nam định', 'nam dinh', 'thanh hóa', 'thanh hoa', 'bình dương', 'binh duong', 'becamex', 'song lam', 'nghe an', 'slna', 'quảng nam', 'quang nam', 'khánh hòa', 'khanh hoa', 'cần thơ', 'can tho']
      
      const matchKeywords = ['live', 'trực tiếp', 'truc tiep', 'xem', 'link', 'stream', 'kèo', 'keo', 'tỷ số', 'ty so', 'kết quả', 'ket qua', 'lịch thi đấu', 'lich thi dau', 'schedule']
      
      const newsKeywords = ['tin', 'news', 'chuyển nhượng', 'chuyen nhuong', 'transfer', 'bình luận', 'binh luan', 'phân tích', 'phan tich', 'analysis']
      
      // Determine best redirect based on query content
      let redirectPath = '/'
      
      if (teamKeywords.some(keyword => query.includes(keyword))) {
        if (matchKeywords.some(keyword => query.includes(keyword))) {
          // Team + match keywords -> live matches
          redirectPath = '/truc-tiep'
        } else if (newsKeywords.some(keyword => query.includes(keyword))) {
          // Team + news keywords -> news
          redirectPath = '/tin-bong-da'
        } else {
          // Just team name -> schedule
          redirectPath = '/lich-thi-dau-bong-da'
        }
      } else if (matchKeywords.some(keyword => query.includes(keyword))) {
        // Match keywords -> live matches
        redirectPath = '/truc-tiep'
      } else if (newsKeywords.some(keyword => query.includes(keyword))) {
        // News keywords -> news
        redirectPath = '/tin-bong-da'
      } else {
        // Generic search -> homepage
        redirectPath = '/'
      }
      
      // Add search parameter for future implementation
      const searchParam = `?q=${encodedQuery}`
      
      // Show user feedback
      if (typeof window !== 'undefined') {
        // Create a temporary notification
        const notification = document.createElement('div')
        notification.innerHTML = `
          <div style="
            position: fixed;
            top: 20px;
            right: 20px;
            background: #10b311;
            color: white;
            padding: 12px 20px;
            border-radius: 8px;
            z-index: 9999;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            font-size: 14px;
            max-width: 300px;
          ">
            🔍 Đang tìm kiếm "${searchQuery.trim()}"...
          </div>
        `
        document.body.appendChild(notification)
        
        setTimeout(() => {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification)
          }
        }, 3000)
      }
      
      // Redirect to the determined page
      router.push(redirectPath + searchParam)
      
    } catch (error) {
      console.error('Search error:', error)
      // Fallback to homepage
      router.push('/')
    } finally {
      setTimeout(() => {
        setIsSearching(false)
      }, 1000) // Keep loading state for better UX
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch(e as any)
    }
  }

  return (
    <form onSubmit={handleSearch} className="flex">
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder="Tìm kiếm trận đấu, đội bóng, tin tức..."
        className="flex-1 px-4 py-2 bg-[#1a1a1a] border border-[#333] rounded-l-lg text-white placeholder-[#999] focus:outline-none focus:ring-2 focus:ring-[#10b311] focus:border-[#10b311]"
        disabled={isSearching}
      />
      <button 
        type="submit"
        disabled={isSearching || !searchQuery.trim()}
        className="px-6 py-2 bg-[#10b311] text-white rounded-r-lg hover:bg-[#0e9e0f] transition-all duration-300 disabled:bg-[#666] disabled:cursor-not-allowed"
      >
        {isSearching ? (
          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
        ) : (
          <FontAwesomeIcon icon={faSearch} />
        )}
      </button>
    </form>
  )
} 